import React, { useEffect, useMemo, useState } from "react";

import { useJsApiLoader } from "@react-google-maps/api";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import LoadingScreen from "../../../components/LoadingScreen";
import { VoyageDetails } from "Components/Tracking";
import { AppConfig } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import useActionShipment from "../../../hooks/useActionShipment";
import { useSecretManager } from "../../../hooks/useSecretManager";
import { ContainerPosition, ContainerStatus } from "../../../models/marine.traffic";
import { Shipment } from "../../../models/shipments";
import { ShipmentsTracking } from "../../../models/shipments/shipments.tracking";
import { ShipmentTrackingVersionService } from "../../../services/shipment.tracking.service";
import env from "../../../util/env";
import { validateAndUpdateFromMarineStatus } from "../../../util/validateMarineStatus";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import useStyles from "./styles";

const ShipeeziLogo = AppConfig.appLogo;

interface PropsApiLoader {
  onChange?: (isLoaded: boolean) => void;
  googleMapsApiKey: string;
  children?: React.ReactNode;
}

const libraries: ("drawing" | "geometry" | "places" | "visualization")[] = ["places"];

const JsApiLoader = (props: PropsApiLoader) => {
  const { onChange, googleMapsApiKey, children } = props;

  const { isLoaded } = useJsApiLoader({
    libraries,
    googleMapsApiKey,
  });

  useEffect(() => {
    if (onChange) onChange(isLoaded);
  }, [isLoaded]);

  if (!isLoaded) {
    return <></>;
  }

  return <>{children}</>;
};

export function ShipmentTrackingClientPage() {
  const { t: translate } = useTranslation();
  const { setMapIsLoaded } = useAppState();
  const { secret } = useSecretManager(env.SECRET_ID);

  const { useUpdateShipmentFromMarineStatus } = useActionShipment();

  const [token, setToken] = useState<string>("");
  const [customerName, setCustomerName] = useState<string>("");
  const [shipment, setShipment] = useState<ShipmentsTracking>();
  const [containerStatus, setContainerStatus] = useState<ContainerStatus>();
  const [containerPosition, setContainerPosition] = useState<ContainerPosition>();

  const classes = useStyles();

  const { refetch, isFetching: fetchingShipment } = useQuery(
    ["shipment.item", token],
    () =>
      ShipmentTrackingVersionService.getInfo({
        token: String(token),
      }),
    {
      enabled: !!token.length,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response.data) {
          const { shipment, container, customerName } = response.data;

          if (customerName) setCustomerName(customerName);
          if (shipment) setShipment(shipment);
          if (container) setContainerStatus(container);
          if (shipment?.shipmentTracking?.vesselPosition) {
            const position = {
              TIMESTAMP: shipment.shipmentTracking.vesselPosition?.updatedAt,
              LAT: shipment.shipmentTracking.vesselPosition?.latitude,
              LON: shipment.shipmentTracking.vesselPosition?.longitude,
            } as ContainerPosition;

            setContainerPosition(position);
          }
        }
      },
    }
  );

  const { mutate: updateShipmentFromMarineStatus } = useUpdateShipmentFromMarineStatus(refetch);

  useEffect(() => {
    if (fetchingShipment)
      validateAndUpdateFromMarineStatus({
        shipment: shipment || ({} as Shipment),
        containerStatus: containerStatus || ({} as ContainerStatus),
        callback: updateShipmentFromMarineStatus,
      });
  }, [containerStatus]);

  useEffect(() => {
    setToken(window.location.href.split("?token=")[1]);
  }, []);

  const showShipment = useMemo(() => {
    return shipment && containerStatus;
  }, [shipment, containerStatus]);

  const renderContent = () => {
    if (!showShipment) return <>{translate("notAvailable")}</>;

    return (
      <main className={classes.mainComponent}>
        <Header
          containerStatus={containerStatus}
          shipment={shipment}
          customerName={customerName}
          logo={ShipeeziLogo}
        />

        <section className={classes.mapSection}>
          {secret?.GOOGLE_MAPS_API_KEY && (
            <JsApiLoader googleMapsApiKey={secret?.GOOGLE_MAPS_API_KEY} onChange={setMapIsLoaded}>
              <VoyageDetails
                shipment={shipment}
                containerStatus={containerStatus}
                containerPosition={containerPosition}
                viewLivePosition={process.env.NODE_DATABASE !== "work"}
              />
            </JsApiLoader>
          )}
        </section>

        <Footer containerPosition={containerPosition} logo={ShipeeziLogo} />
      </main>
    );
  };

  return (
    <div className={classes.wrapper}>
      <LoadingScreen
        showing={fetchingShipment}
        message={translate("yourShipmentInformationIsLoading")}
      />
      {renderContent()}
    </div>
  );
}
